import React from 'react';
import { generatePlacementInterpretation } from '../../lib/placementInterpretation';
import { formatPlacementLabel } from '../../lib/chartFormat';

const HOUSE_MEANINGS = {
  1: 'Identity and first approach',
  2: 'Resources and self-worth',
  3: 'Learning and communication',
  4: 'Roots and foundations',
  5: 'Creativity and expression',
  6: 'Routines and craft',
  7: 'Relationships and mirrors',
  8: 'Depth and shared bonds',
  9: 'Belief and perspective',
  10: 'Direction and contribution',
  11: 'Community and future',
  12: 'Retreat and integration',
};

const ASPECT_LABELS = {
  conjunction: 'Conjunction',
  opposition: 'Opposition',
  trine: 'Trine',
  square: 'Square',
  sextile: 'Sextile',
};

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function formatPlanetName(key) {
  const map = {
    sun: 'Sun',
    moon: 'Moon',
    mercury: 'Mercury',
    venus: 'Venus',
    mars: 'Mars',
    jupiter: 'Jupiter',
    saturn: 'Saturn',
    uranus: 'Uranus',
    neptune: 'Neptune',
    pluto: 'Pluto',
    northnode: 'North Node',
    lilith: 'Lilith',
    chiron: 'Chiron',
  };
  return map[key] || key;
}

/**
 * Immediate feedback panel shown directly below the control that triggered the selection.
 * Ensures the user sees the result where they interacted.
 */
const SelectionFeedbackCard = ({
  chart,
  selectedPlanetKey,
  selectedHouse,
  selectedAspectId,
  selectedAngleKey,
}) => {
  if (!chart) return null;
  const placements = chart.planets || [];
  const houses = chart.houses || [];
  const aspects = chart.aspects || [];

  if (selectedAngleKey) {
    const label = String(selectedAngleKey).toUpperCase();
    const angle = chart.angles?.[label.toLowerCase()] || null;
    return (
      <div className="chart-selection-feedback" role="status" aria-live="polite">
        <p className="chart-selection-feedback-title">
          <strong>{label}</strong> — {angle ? `${angle.sign} ${angle.degreeText}` : 'Chart angle'}
        </p>
        <p className="chart-selection-feedback-summary">
          This angle sets a key structural axis in chart interpretation.
        </p>
      </div>
    );
  }

  if (selectedAspectId) {
    const aspect = aspects.find((item) => item.id === selectedAspectId);
    if (!aspect) return null;
    const p1Label = formatPlanetName(aspect.p1);
    const p2Label = formatPlanetName(aspect.p2);
    const typeLabel = ASPECT_LABELS[aspect.type] || aspect.type;
    return (
      <div className="chart-selection-feedback" role="status" aria-live="polite">
        <p className="chart-selection-feedback-title">
          <strong>{p1Label}</strong> {typeLabel} <strong>{p2Label}</strong>
        </p>
        <p className="chart-selection-feedback-summary">
          How these two functions interact. Orb: {Number(aspect.orb || 0).toFixed(2)}°.
        </p>
      </div>
    );
  }

  if (selectedHouse) {
    const house = houses.find((item) => Number(item.house) === Number(selectedHouse));
    const planetsInHouse = placements.filter((item) => Number(item.house) === Number(selectedHouse));
    const h = ordinalHouse(selectedHouse);
    const theme = HOUSE_MEANINGS[selectedHouse] || 'Life arena';
    return (
      <div className="chart-selection-feedback" role="status" aria-live="polite">
        <p className="chart-selection-feedback-title">
          <strong>{h} House</strong> — {theme}
        </p>
        <p className="chart-selection-feedback-summary">
          {house?.sign || '—'} on the cusp.
          {planetsInHouse.length > 0
            ? ` Active here: ${planetsInHouse.map((p) => `${p.name} in ${p.sign}`).join(', ')}.`
            : ''}
        </p>
      </div>
    );
  }

  const placement = placements.find((item) => item.key === selectedPlanetKey) || placements[0];
  if (!placement) return null;
  const interpretation = generatePlacementInterpretation({ placement });
  if (!interpretation) return null;

  return (
    <div className="chart-selection-feedback" role="status" aria-live="polite">
      <p className="chart-selection-feedback-title">
        <strong>{formatPlacementLabel(placement)}</strong>
      </p>
      <p className="chart-selection-feedback-summary">{interpretation.corePattern}</p>
    </div>
  );
};

export default SelectionFeedbackCard;
